import React from "react";
import { Box, Typography, Card, Chip, Divider, Stack } from "@mui/joy";
import DnsRoundedIcon from "@mui/icons-material/DnsRounded";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import { keyframes } from "@mui/material";

// Keyframe animations for modern feel
const pulseGlow = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 14px rgba(239, 68, 68, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
`;

const floatAnim = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
`;

const bgMesh = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const Maintenance = () => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 2, sm: 3, md: 4 },
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#070b14",
        backgroundImage: `
          radial-gradient(at 10% 15%, rgba(59, 130, 246, 0.12) 0px, transparent 50%),
          radial-gradient(at 90% 85%, rgba(239, 68, 68, 0.14) 0px, transparent 50%),
          radial-gradient(at 50% 50%, rgba(11, 15, 25, 0.95) 0px, transparent 100%)
        `,
        backgroundSize: "200% 200%",
        animation: `${bgMesh} 18s ease infinite`,
        fontFamily: "'Inter', 'Poppins', -apple-system, sans-serif",
      }}
    >
      {/* Decorative ambient blurred glow */}
      <Box
        sx={{
          position: "absolute",
          top: "15%",
          left: "20%",
          width: "360px",
          height: "360px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0) 70%)",
          filter: "blur(70px)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "15%",
          right: "15%",
          width: "380px",
          height: "380px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(239, 68, 68, 0.14) 0%, rgba(239, 68, 68, 0) 70%)",
          filter: "blur(70px)",
          pointerEvents: "none",
        }}
      />

      {/* Main Glassmorphic Container Card */}
      <Card
        variant="outlined"
        sx={{
          maxWidth: "680px",
          width: "100%",
          borderRadius: "24px",
          p: { xs: 3, sm: 4, md: 5 },
          backgroundColor: "rgba(13, 18, 30, 0.82)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          border: "1px solid rgba(255, 255, 255, 0.09)",
          boxShadow: "0 25px 60px -15px rgba(0, 0, 0, 0.8), 0 0 30px rgba(239, 68, 68, 0.08)",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Status Header Badge */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Chip
            size="md"
            variant="soft"
            sx={{
              px: 2,
              py: 0.8,
              borderRadius: "50px",
              backgroundColor: "rgba(239, 68, 68, 0.12)",
              border: "1px solid rgba(239, 68, 68, 0.28)",
              color: "#fca5a5",
              fontSize: "12px",
              fontWeight: 600,
              letterSpacing: "0.6px",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#ef4444",
                animation: `${pulseGlow} 2s infinite`,
              }}
            />
            HTTP 503 • SERVER UNAVAILABLE
          </Chip>
        </Box>

        {/* Server Icon with Floating Animation */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "88px",
            height: "88px",
            borderRadius: "24px",
            background: "linear-gradient(135deg, rgba(239, 68, 68, 0.18) 0%, rgba(59, 130, 246, 0.15) 100%)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            color: "#f87171",
            mx: "auto",
            mb: 2.5,
            animation: `${floatAnim} 4s ease-in-out infinite`,
            boxShadow: "0 12px 28px rgba(239, 68, 68, 0.2)",
          }}
        >
          <DnsRoundedIcon sx={{ fontSize: "46px" }} />
        </Box>

        {/* Primary Headline */}
        <Typography
          level="h1"
          sx={{
            fontSize: { xs: "24px", sm: "30px", md: "34px" },
            fontWeight: 800,
            letterSpacing: "-0.5px",
            color: "#ffffff",
            mb: 1.5,
            lineHeight: 1.2,
            background: "linear-gradient(135deg, #ffffff 40%, #cbd5e1 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Oops the server is currently down
        </Typography>

        {/* Server-Side Description */}
        <Typography
          level="body-md"
          sx={{
            color: "#94a3b8",
            fontSize: { xs: "14px", sm: "15px" },
            lineHeight: 1.6,
            maxWidth: "520px",
            mx: "auto",
            mb: 3.5,
          }}
        >
          The application host is temporarily unreachable. The gateway is unable to establish an upstream connection with the primary server node.
        </Typography>

        <Divider sx={{ my: 2.5, borderColor: "rgba(255, 255, 255, 0.08)" }} />

        {/* Server-Side Diagnostics & Contact Box */}
        <Box
          sx={{
            mt: 1,
            p: { xs: 2.5, sm: 3 },
            borderRadius: "16px",
            backgroundColor: "rgba(10, 15, 26, 0.8)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
            textAlign: "left",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.2 }}>
            <TerminalRoundedIcon sx={{ color: "#60a5fa", fontSize: "22px" }} />
            <Typography
              level="h3"
              sx={{
                fontSize: { xs: "16px", sm: "17px" },
                fontWeight: 700,
                color: "#e2e8f0",
                letterSpacing: "-0.2px",
              }}
            >
              Contact to technical team
            </Typography>
          </Box>

          <Typography
            level="body-sm"
            sx={{
              color: "#94a3b8",
              fontSize: "13.5px",
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            The server is experiencing an upstream connection timeout. If this issue persists, please contact to technical team for server diagnostics and restoration.
          </Typography>

          {/* Diagnostic Info */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{
              pt: 1.8,
              borderTop: "1px dashed rgba(255, 255, 255, 0.08)",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CloudOffRoundedIcon sx={{ fontSize: "16px", color: "#64748b" }} />
              <Typography level="body-xs" sx={{ color: "#64748b", fontSize: "12px" }}>
                Host Status: Offline / 503 Unreachable
              </Typography>
            </Box>
            <Typography level="body-xs" sx={{ color: "#64748b", fontSize: "12px", fontFamily: "monospace" }}>
              NODE: srv-gateway-node-01
            </Typography>
          </Stack>
        </Box>

        {/* Footer info */}
        <Box sx={{ mt: 3 }}>
          <Typography
            level="body-xs"
            sx={{
              color: "#475569",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.8,
            }}
          >
            <HubRoundedIcon sx={{ fontSize: "14px" }} /> Server Infrastructure Monitoring &bull; Gateway 503
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};

export default Maintenance;
